// Setup sidebar (design spec 2026-10-19 §4; Setup UX pass 2026-10-21; SETUP-REGROUP, review
// #18): a search box, Overview (the Setup home), then one caption per group of the Setup catalog
// (features/settings/setup-catalog.ts) — Company, People & access, Structure, Measurement,
// Finance, Workforce, Integrations, Security & audit — each row its own URL (back/forward work)
// and the current one `aria-current="page"`. Rows whose add-on is off are absent; pages in other
// modes are not rows, but the search finds them, marked ↗ (UX-A 0.8). Personal
// settings are not here: they are the account menu's "Your settings". Data entry lives in Cadence;
// while Cadence is off (`kpi`), hours and import come back here under Measurement.
import { NavList, NavRow, SearchField, SidebarCaption, SidebarPanel } from "@companyos/ui";
import { Link, useRouterState } from "@tanstack/react-router";
import { ArrowUpRightIcon, LayoutGridIcon } from "lucide-react";
import { useState } from "react";
import type { SidebarProps } from "../../extensions/registry";
import {
  isListed,
  MODE_LABEL,
  matchSetup,
  SETUP_GROUPS,
  type SetupEntry,
  setupEntries,
} from "../../features/settings/setup-catalog";
import { useFeature } from "../../features/use-feature";
import { keepFocus } from "../../focus/focus";
import { ManagerDataRows } from "./data-rows";

function EntryRow({ entry, slug }: { entry: SetupEntry; slug: string }) {
  return (
    <NavRow
      icon={entry.icon}
      label={entry.label}
      {...(entry.mode
        ? {
            trailing: (
              <ArrowUpRightIcon
                aria-label={`opens ${MODE_LABEL[entry.mode]}`}
                className="size-3.5 text-faint"
              />
            ),
          }
        : {})}
      render={
        <Link
          to={entry.to as never}
          params={{ slug } as never}
          search={
            ((prev: Record<string, unknown>) => ({ ...keepFocus(prev), ...entry.search })) as never
          }
          // Rows are exact: Finance access is not lit on Prices.
          activeOptions={{ includeSearch: false, exact: true }}
        />
      }
    />
  );
}

export function SetupSidebar({ company, children }: SidebarProps) {
  const slug = company.slug;
  const kpi = useFeature("kpi").enabled;
  const memory = useFeature("memory").enabled;
  const [query, setQuery] = useState("");
  // The Setup home has its own, larger search over the same list; the sidebar's then steps aside.
  const onHome = useRouterState({
    select: (s) => /^\/c\/[^/]+\/setup\/?$/.test(s.location.pathname),
  });
  const searching = !onHome && query.trim() !== "";
  // UX-A 0.8: the rows are Setup pages only; a search also finds the pages in other modes (↗)
  // and a tab's other name (Connection checks).
  const all = setupEntries({ kpi, memory });
  const rows = searching
    ? matchSetup(
        all.filter((e) => !e.hash),
        query,
      )
    : all.filter((e) => isListed(e) && e.sidebar !== false);
  return (
    <SidebarPanel title="Settings">
      {onHome ? null : (
        <div className="px-3 pt-1 pb-2">
          <SearchField
            className="w-full"
            value={query}
            placeholder="Search settings…"
            aria-label="Search settings"
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
      )}
      {searching ? null : (
        <NavList>
          <NavRow
            icon={LayoutGridIcon}
            label="Overview"
            render={
              <Link
                to="/c/$slug/setup"
                params={{ slug }}
                search={keepFocus}
                activeOptions={{ includeSearch: false, exact: true }}
              />
            }
          />
        </NavList>
      )}
      {SETUP_GROUPS.map((g) => {
        const group = rows.filter((e) => e.group === g.key);
        // Data entry sits under Measurement while Cadence (`kpi`) is off.
        const data = g.key === "measurement" && !kpi && !searching;
        if (!group.length && !data) return null;
        return (
          <div key={g.key} data-slot="setup-nav-group" data-group={g.key}>
            <SidebarCaption label={g.label} />
            {group.length ? (
              <NavList
                aria-label={g.label}
                {...(g.key === "finance" ? { "data-slot": "setup-finance-nav" } : {})}
              >
                {group.map((e) => (
                  <EntryRow key={e.key} entry={e} slug={slug} />
                ))}
              </NavList>
            ) : null}
            {data ? <ManagerDataRows company={company} /> : null}
          </div>
        );
      })}
      {searching && !rows.length ? (
        <p className="px-4 py-1 text-xs text-muted-foreground">No setting matches.</p>
      ) : null}
      {children}
    </SidebarPanel>
  );
}
